import React, { useCallback, useMemo } from 'react';
import type { AnalysisProps } from '../../types';
import { useRecoilValue } from 'recoil';
import {
  blockDisplayHeader,
  blockFonts,
  getCustomFontSize,
  studioPrintOrientationType,
  type StudioRequestSubject,
} from 'venn-state';
import { pairwiseCorrelationExtractor } from '../../../studio-blocks/components/charts/pairwise-correlation/dataExtractors';
import { type ExcelCell } from 'venn-utils';
import useExportUpdate from '../../../studio-blocks/logic/useExportUpdate';
import type { TabularDataRow } from '../../../studio-blocks/components/charts/pairwise-correlation/types';
import { EllipsisTooltipSpan } from 'venn-ui-kit';
import { formatExportableSubjectWithOptionalFee } from '../../../legend';
import { useGetRequestSubjectFee } from '../../utils/hooks/use-advisory-fee';
import { ColumnAlign } from '../../../basictable';
import { compact } from 'lodash';
import VerticalHeader from '../../../studio-blocks/components/charts/pairwise-correlation/VerticalHeader';
import { pairwiseCorrelationCellRenderer } from '../../../studio-blocks/components/charts/pairwise-correlation/cellRenderers';
import CorrelationColorLegend from '../../../studio-blocks/components/charts/pairwise-correlation/CorrelationColorLegend';
import type { Analysis } from 'venn-api';
import {
  CorrelationGrid,
  FIRST_COLUMN_INNER_WIDTH,
  FIRST_COLUMN_OUTER_WIDTH_PX,
} from '../../components/visualizations/grids/correlation-grid';
import { useBlockSubjectSuperscripts } from '../../components/core/block-subject-footnotes-and-superscripts';
import { useIsCorrelationLabelFull } from '../../../studio-blocks/logic/useCorrelationLabelDisplay';

type PairwiseCorrelationProps = Omit<AnalysisProps, 'analyses'> & {
  analysis: Analysis | undefined;
};

const CorrelationLabel = ({
  label,
  subject,
  isBenchmark,
  isFullLabelDisplay,
}: {
  label: string;
  subject: StudioRequestSubject;
  isBenchmark: boolean;
  isFullLabelDisplay: boolean;
}) => {
  const superscripts = useBlockSubjectSuperscripts({
    subject,
    isBenchmark,
  });
  const labelContent = [<sup>{superscripts}</sup>, label];

  return isFullLabelDisplay ? (
    <span>{labelContent}</span>
  ) : (
    <span>
      <EllipsisTooltipSpan usePortal maxWidth={FIRST_COLUMN_INNER_WIDTH}>
        {labelContent}
      </EllipsisTooltipSpan>
    </span>
  );
};
export const PairwiseCorrelation = ({ id, requests, analysis, exportMetaData }: PairwiseCorrelationProps) => {
  const tableFont = useRecoilValue(blockFonts.blockTableData(id));
  const legendFont = useRecoilValue(blockFonts.blockChartLegend(id));
  const printOrientation = useRecoilValue(studioPrintOrientationType);
  const header = useRecoilValue(blockDisplayHeader(id));
  const isFullLabelDisplay = useIsCorrelationLabelFull();
  const getRequestSubjectFee = useGetRequestSubjectFee();
  const subjects = useMemo(() => requests.map((req) => req.subject), [requests]);

  const data = useMemo(
    () => pairwiseCorrelationExtractor(subjects, requests, analysis),
    [subjects, requests, analysis],
  );

  const excelDataFn = useCallback(() => {
    const isRelative = requests[0]?.relative;
    const benchmarks = requests.map((r) => r.benchmark);
    const headerRow: ExcelCell[] = data.map((data, index) => ({
      value: isRelative ? `${data.label} (Relative to ${benchmarks[index]?.name ?? '--'})` : data.label,
      bold: true,
    }));
    const body: ExcelCell[][] = data.map((row) => {
      const cellData: ExcelCell[] = [];
      for (let subjectIdx = 0; subjectIdx < requests.length; subjectIdx++) {
        cellData.push({
          value: row[subjectIdx]?.value ?? '--',
          percentage: row?.type === 'percentage',
          digits: 2,
        });
      }
      return [{ value: row.label, bold: true }, ...cellData];
    });
    return [[{ value: header, bold: true }, ...headerRow], ...body];
  }, [data, requests, header]);

  useExportUpdate({ selectedRefId: id, exportMetaData, excelDataFn });

  const correlationCellRenderer = useCallback(
    (rowData: TabularDataRow) => (
      <CorrelationLabel
        label={rowData.label}
        subject={rowData.subject}
        isBenchmark={rowData.isBenchmark}
        isFullLabelDisplay={isFullLabelDisplay}
      />
    ),
    [isFullLabelDisplay],
  );

  const columns = useMemo(
    () =>
      compact([
        {
          label: 'Correlation',
          accessor: 'label',
          headerStyle: {
            maxWidth: FIRST_COLUMN_OUTER_WIDTH_PX,
            fontSize: `${tableFont.fontSizePt}pt`,
          },
          cellRenderer: correlationCellRenderer,
        },
        ...subjects.map((subject, colNum) => {
          const headerRenderer = (_: unknown, data: TabularDataRow[]) => (
            <VerticalHeader>{data[colNum]!.label}</VerticalHeader>
          );
          return {
            label: formatExportableSubjectWithOptionalFee(subject, false, getRequestSubjectFee({ subject })),
            align: ColumnAlign.RIGHT,
            cellRenderer: pairwiseCorrelationCellRenderer(colNum, 0, undefined),
            headerRenderer,
          };
        }),
      ]),
    [correlationCellRenderer, subjects, tableFont, getRequestSubjectFee],
  );

  return (
    <>
      <CorrelationGrid
        columns={columns}
        data={data}
        compactRows={printOrientation === 'LANDSCAPE'}
        fontSize={getCustomFontSize(tableFont)}
        isFullSizeLabels={isFullLabelDisplay}
      />
      <CorrelationColorLegend fontSize={getCustomFontSize(legendFont)} />
    </>
  );
};
